import { FC, ReactNode, useMemo } from 'react';
import { Avatar, Button, Flex } from 'antd';
import {
    TbBuilding,
    TbCalendarSearch,
    TbChartLine,
    TbCircleDashedX,
    TbClockSearch,
    TbCornerDownRight,
    TbCube,
    TbFileText,
    TbLayersIntersect,
    TbLayoutGrid,
    TbNews,
    TbTransactionDollar,
    TbUser,
    TbWorldDollar,
} from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    type RecentSearchGroup,
    type RecentSearchQuery,
    type RecentSearchResource,
    type RecentSearchScope,
} from '@/hooks/useRecentSearches';
import { CompactVirtualizedList, EmptySection, FundIcon, TickerExchangeTag } from '@/shared';
import { ellipsisStyling, resolveCorporateLogo } from '@/utilities';

import { SEARCH_HORIZONTAL_GUTTER, searchTabDefinitions } from '../../../../searchTabs';

interface RecentSearchHistoryProps {
    groups: RecentSearchGroup[];
    onClear: (scope: RecentSearchScope) => void;
    queries: RecentSearchQuery[];
    resources: RecentSearchResource[];
    scope: RecentSearchScope;
}

type RecentSearchHistoryItem =
    | { key: string; kind: 'query'; query: RecentSearchQuery }
    | { key: string; kind: 'resource'; resource: RecentSearchResource };

const SCOPE_PRESENTATION: Record<RecentSearchScope, { icon: ReactNode; label: string }> = {
    all: { icon: <TbClockSearch />, label: 'All Search' },
    companies: { icon: <TbBuilding />, label: 'Companies' },
    documents: { icon: <TbFileText />, label: 'Documents' },
    economic: { icon: <TbTransactionDollar />, label: 'Economics' },
    events: { icon: <TbCalendarSearch />, label: 'Events' },
    news: { icon: <TbNews />, label: 'News' },
    people: { icon: <TbUser />, label: 'People' },
    trade: { icon: <TbWorldDollar />, label: 'Global Trade' },
    workspace: { icon: <TbLayoutGrid />, label: 'Workspace' },
};

const SCOPE_ORDER = new Map(searchTabDefinitions.map((definition, index) => [definition.key, index]));

const resourceIcon = (resource: RecentSearchResource): ReactNode => {
    switch (resource.resourceType) {
        case 'fund':
            return <FundIcon />;
        case 'entity':
            return <TbLayersIntersect />;
        case 'index':
            return <TbChartLine />;
        case 'document':
            return <TbFileText />;
        case 'economic':
            return <TbTransactionDollar />;
        case 'event':
            return <TbCalendarSearch />;
        case 'news':
            return <TbNews />;
        case 'person':
            return <TbUser />;
        case 'trade':
            return <TbWorldDollar />;
        case 'workspace':
            return <TbLayoutGrid />;
        case 'other':
            return <TbCube />;
        default:
            return <TbBuilding />;
    }
};

const groupItems = (group: RecentSearchGroup): RecentSearchHistoryItem[] => [
    ...group.resources.map((resource) => ({
        key: `resource:${resource.scope}:${resource.resourceType}:${resource.id}`,
        kind: 'resource' as const,
        resource,
    })),
    ...group.queries.map((query) => ({
        key: `query:${query.scope}:${query.query}`,
        kind: 'query' as const,
        query,
    })),
];

const RecentSearchHistory: FC<RecentSearchHistoryProps> = ({
    groups: allGroups,
    onClear,
    queries,
    resources,
    scope,
}) => {
    const { token } = useAntDesignTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const groups = useMemo(() => {
        if (scope !== 'all') {
            return queries.length || resources.length ? [{ queries, resources, scope }] : [];
        }

        return [...allGroups].sort(
            (first, second) => (SCOPE_ORDER.get(first.scope) ?? 100) - (SCOPE_ORDER.get(second.scope) ?? 100),
        );
    }, [allGroups, queries, resources, scope]);

    const selectQuery = (query: RecentSearchQuery) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('q', query.query);
        searchParams.delete('openResult');

        navigate({ pathname: `/search/${query.scope}`, search: searchParams.toString() });
    };

    const selectResource = (resource: RecentSearchResource) => {
        if (resource.external) {
            window.open(resource.path, '_blank', 'noopener,noreferrer');
            return;
        }

        navigate(resource.path);
    };

    if (groups.length === 0) {
        return (
            <EmptySection
                description='Completed searches and resources you open from Search will appear here.'
                height='100%'
                icon={<TbClockSearch size='75%' style={{ color: token.colorTextTertiary }} />}
                label='No Recent Searches'
            />
        );
    }

    return (
        <div
            style={{
                boxSizing: 'border-box',
                minHeight: 0,
                padding: `0.5rem ${SEARCH_HORIZONTAL_GUTTER}`,
                width: '100%',
            }}
        >
            {scope === 'all' ? (
                <Flex
                    align='center'
                    gap='0.25rem'
                    style={{ color: token.colorTextTertiary, padding: '0 0.325rem 0.5rem' }}
                >
                    <TbClockSearch />
                    <small>Recent Searches</small>
                </Flex>
            ) : null}
            <Flex gap='0.75rem' vertical>
                {groups.map((group) => {
                    const presentation = SCOPE_PRESENTATION[group.scope];
                    const items = groupItems(group);

                    return (
                        <CompactVirtualizedList
                            animated={false}
                            bodyFooter={
                                <div
                                    style={{
                                        padding: '0.25rem',
                                    }}
                                >
                                    <Button
                                        block
                                        icon={<TbCircleDashedX />}
                                        onClick={() => onClear(group.scope)}
                                        size='small'
                                        style={{ color: token.colorTextTertiary, justifyContent: 'flex-start' }}
                                        type='text'
                                    >
                                        Clear Recent Searches
                                    </Button>
                                </div>
                            }
                            collapsible={scope === 'all'}
                            dataSource={items}
                            getItemKey={(item) => item.key}
                            headerStyle={{ padding: '0.25rem 0.5rem' }}
                            icon={scope === 'all' ? presentation.icon : <TbClockSearch />}
                            key={group.scope}
                            renderItem={(item) => (
                                <CompactVirtualizedList.Item key={item.key} style={{ padding: 0 }}>
                                    {item.kind === 'resource' ? (
                                        <Flex
                                            align='center'
                                            style={{
                                                height: '2.5rem',
                                                minWidth: 0,
                                                width: '100%',
                                            }}
                                        >
                                            <Button
                                                block
                                                onClick={() => selectResource(item.resource)}
                                                style={{
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                    height: '2.5rem',
                                                    justifyContent: 'flex-start',
                                                    minWidth: 0,
                                                    width: '100%',
                                                }}
                                                type='text'
                                            >
                                                <Avatar
                                                    icon={resourceIcon(item.resource)}
                                                    shape='square'
                                                    size={24}
                                                    src={resolveCorporateLogo(
                                                        item.resource.website,
                                                        item.resource.logoUrl,
                                                        {},
                                                        {
                                                            name: item.resource.name,
                                                            ticker: item.resource.symbol,
                                                        },
                                                    )}
                                                    style={{
                                                        backgroundColor: token.colorBgContainer,
                                                        border: `1px solid ${token.colorBorder}`,
                                                        flex: '0 0 auto',
                                                    }}
                                                />
                                                <span style={{ ...ellipsisStyling, flex: '0 1 auto', minWidth: 0 }}>
                                                    {item.resource.name}
                                                </span>
                                                {(item.resource.resourceType === 'company' ||
                                                    item.resource.resourceType === 'fund') &&
                                                item.resource.symbol ? (
                                                    <TickerExchangeTag
                                                        assetType={
                                                            item.resource.resourceType === 'fund' ? 'fund' : 'company'
                                                        }
                                                        companyName={item.resource.name}
                                                        compact
                                                        exchange={item.resource.exchange}
                                                        styles={{ flex: '0 0 auto', margin: 0 }}
                                                        symbol={item.resource.symbol}
                                                        website={item.resource.website}
                                                    />
                                                ) : null}
                                            </Button>
                                        </Flex>
                                    ) : (
                                        <Button
                                            block
                                            icon={<TbCornerDownRight />}
                                            onClick={() => selectQuery(item.query)}
                                            style={{
                                                height: '2.5rem',
                                                justifyContent: 'flex-start',
                                            }}
                                            type='text'
                                        >
                                            <span style={{ ...ellipsisStyling, minWidth: 0 }}>{item.query.query}</span>
                                        </Button>
                                    )}
                                </CompactVirtualizedList.Item>
                            )}
                            showCount
                            stickyHeader={false}
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: token.borderRadius,
                                overflow: 'hidden',
                            }}
                            title={scope === 'all' ? presentation.label : 'Recent Searches'}
                        />
                    );
                })}
            </Flex>
        </div>
    );
};

export default RecentSearchHistory;
